// Talking to the AI service.
//
// Not on apiInstance, and deliberately: the chat endpoint streams server-sent
// events, and axios in the browser buffers the whole body before resolving —
// which would turn a streamed answer into a long pause followed by a wall of
// text. fetch exposes the body as a stream, so the panel can paint as the
// answer arrives.
//
// The session and suggestion calls are plain JSON and could have gone through
// apiInstance, but keeping all three together means the token and base URL are
// read the same way for all of them.

import { AI_CHAT, AI_SESSION, AI_SUGGESTIONS, BASE_URL } from "store/api/constApi";
import { TOKEN_STORAGE_KEY } from "store/api/axiosApi";

export interface AiAction {
  action: string;
  topic: string;
  title: string;
  /** Relative to /:clusterId — the caller prefixes the cluster. */
  route: string;
  steps: string[];
}

export interface AiTopic {
  topic: string;
  title: string;
  route: string;
  /**
   * The guide behind the topic. Older service builds send only the three
   * fields above; the browse tab then falls back to navigating instead of
   * opening a reader, so the panel works on either side of a deploy.
   */
  description?: string;
  steps?: string[];
}

export interface AiSuggestions {
  questions: string[];
  topics: AiTopic[];
}

export interface AiTurn {
  role: "user" | "assistant";
  content: string;
}

/**
 * Where the customer is in the portal when they ask. Sent so "why can't I
 * remove this card?" can be understood as being about the screen in front of
 * them. Only the route and its screen name — no data from the page.
 */
export interface AiPage {
  /** Relative to /:clusterId, like a topic route. */
  route: string;
  title?: string;
}

/**
 * Screen names for the routes a signed-in customer can be on, keyed by the
 * first path segment after the cluster id. Matches the sidebar and the
 * service's own flow catalogue; a route not listed here is still sent, just
 * without a friendly name.
 */
const PAGE_TITLES: Record<string, string> = {
  dashboard: "Dashboard",
  payments: "Payments",
  "subscriptions-plans": "Subscriptions & Plans",
  "action-center": "Action Center",
  "payment-methods": "Payment Methods",
  documents: "Documents",
  "make-payment": "Make a payment",
  "my-profile": "My profile",
  "support-help": "Support & Help",
  notification: "Notifications",
};

/**
 * Where the customer is asking from.
 *
 * Read at send time rather than kept anywhere: the panel stays open across
 * navigation, so the page at the moment of the question is the one that
 * matters. Only the route and its name — never anything on the page.
 */
export function currentPage(): AiPage | null {
  // Portal paths are /:clusterId/<screen>/…; anything shorter (login,
  // payment links, invalid) has no signed-in screen to describe.
  const parts = window.location.pathname.split("/");
  if (parts.length < 3 || !parts[2]) return null;

  const route = `/${parts.slice(2).join("/")}`;
  const title = PAGE_TITLES[parts[2]];
  return title ? { route, title } : { route };
}

/** Turns replayed for follow-up context. The service bounds this again. */
export const HISTORY_TURNS = 6;

/**
 * The transcript as the service should see it again.
 *
 * Failed turns hold the error shown to the customer ("Sorry — something went
 * wrong"), and an aborted turn holds nothing at all. Neither is something the
 * assistant said, so replaying them as history would have the model reading
 * its own error page back as context. Filtered before the cap, so a run of
 * failures cannot push real turns out of the window.
 */
export function toHistory(
  messages: ReadonlyArray<{
    role: "user" | "assistant";
    content: string;
    failed?: boolean;
  }>
): AiTurn[] {
  return messages
    .filter((message) => !message.failed && message.content.trim() !== "")
    .slice(-HISTORY_TURNS)
    .map(({ role, content }) => ({ role, content }));
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Whether this customer's token is accepted by the AI service.
 *
 * Asked once before the button is shown. The alternative — show the button and
 * find out on the first question — puts the failure in front of someone who
 * has already typed something.
 */
export async function fetchAiSession(signal?: AbortSignal): Promise<boolean> {
  try {
    const response = await fetch(`${BASE_URL}${AI_SESSION}`, {
      headers: authHeaders(),
      signal,
    });

    if (!response.ok) {
      unavailable(`the service answered ${response.status}`);
      return false;
    }

    const body = await response.json();
    if (!body?.Success) {
      unavailable(body?.Message || "the service declined the session");
      return false;
    }

    return true;
  } catch (error: any) {
    if (signal?.aborted) return false;
    // Includes the service being unreachable, CORS, and DNS.
    unavailable(error?.message || "the request failed");
    return false;
  }
}

/**
 * Say why the button is not there.
 *
 * A missing assistant is not worth an error in front of a customer — the
 * button simply does not appear. But hiding it on every failure with no signal
 * at all makes "I don't see the button" impossible to diagnose without
 * reading this file, which is precisely what happened the first time. One
 * console line costs the customer nothing and answers the question.
 */
function unavailable(reason: string) {
  // eslint-disable-next-line no-console
  console.info(
    `[assistant] hidden: ${reason}. Checked ${BASE_URL}${AI_SESSION}.`
  );
}

export async function fetchAiSuggestions(
  signal?: AbortSignal
): Promise<AiSuggestions | null> {
  try {
    const response = await fetch(`${BASE_URL}${AI_SUGGESTIONS}`, {
      headers: authHeaders(),
      signal,
    });
    if (!response.ok) return null;
    const body = await response.json();
    return body?.Result ?? null;
  } catch {
    return null;
  }
}

interface AskCallbacks {
  onToken: (text: string) => void;
  onAction: (action: AiAction) => void;
}

/**
 * Ask one question and stream the answer.
 *
 * Resolves with the complete answer once the stream closes. Throws on a
 * transport failure or a non-OK status; the caller turns that into a message
 * in the transcript rather than a toast, because the question is still on
 * screen and the error belongs next to it.
 */
export async function askAi(
  question: string,
  history: AiTurn[],
  { onToken, onAction }: AskCallbacks,
  signal?: AbortSignal,
  page?: AiPage | null
): Promise<string> {
  const response = await fetch(`${BASE_URL}${AI_CHAT}`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    // page is omitted rather than sent null so a service build that predates
    // it sees exactly the request it always got.
    body: JSON.stringify({ question, history, ...(page ? { page } : {}) }),
    signal,
  });

  if (!response.ok || !response.body) {
    // The service refuses an over-long or empty question with JSON rather than
    // a stream, and that message is written for the customer.
    const message = await response
      .json()
      .then((body) => body?.Message)
      .catch(() => null);
    throw new Error(message || "Sorry — I could not answer that just now.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let answer = "";

  // SSE frames are separated by a blank line, and a chunk boundary can fall
  // anywhere — including mid-frame. Everything after the last blank line stays
  // in the buffer until the rest of it arrives.
  const drain = (flush: boolean) => {
    const frames = buffer.split("\n\n");
    buffer = flush ? "" : frames.pop() ?? "";

    for (const frame of frames) {
      if (!frame.trim()) continue;

      const event = /^event: (.*)$/m.exec(frame)?.[1];
      const raw = /^data: (.*)$/m.exec(frame)?.[1];
      if (!event || !raw) continue;

      let data: any;
      try {
        data = JSON.parse(raw);
      } catch {
        continue;
      }

      if (event === "token") {
        answer += data.text;
        onToken(data.text);
      } else if (event === "action") {
        onAction(data as AiAction);
      } else if (event === "done") {
        // Authoritative: the tokens are for painting, this is the transcript.
        answer = data.answer ?? answer;
      } else if (event === "error") {
        throw new Error(data.message);
      }
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    drain(false);
  }

  buffer += decoder.decode();
  drain(true);

  return answer;
}
